import { OneActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { ScoreWithVariation } from "src/services/ScoreService";
import { ActionPlanScoreTargets, SsqPost } from "src/api/client-api/ActionPlan/ActionPlan/useGetOneActionPlan";
import { Campaign } from "src/api/client-api/useCampaign";
import { MainScore, ScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { OlapScoresService } from "../OlapScores/OlapScoresService";

type ScoreLabel = string;
export type APScore = {
    id: number;
    campaign?: Campaign | undefined;
    ssqVersionId: number | undefined;
    scoreByLabel: ScoreLabel;
    score: ScoreValue;
    score2: MainScore;
};
type ScoreID = number;

type Goal = {
    value: number | undefined;
    completionPercentage: number;
    goalReductionPercentage: number;
};
type AggPosts = {
    lastItem: APScore | null;
    details: APScore[];
};
export type APMetadata = {
    actionPlan: OneActionPlan;

    goal: Goal | null;
    startingPost: APScore;

    scoresWithActionPost: AggPosts;
    scoresWithoutActionPost: AggPosts;
};

export type ActionPlanData = {
    scoreId: ScoreID;
    variationId: ScoreID | null;
    // unit: string;
    // variations: [],

    metadata: APMetadata;
};

type Options = {
    actionPlan: OneActionPlan | undefined;
    scores: ScoreWithVariation[];
};
export const useActionPlanData = (options: Options): ActionPlanData[] => {
    const { actionPlan, scores } = options;
    if (actionPlan === undefined) {
        return [];
    }

    const actionPlanByScores = scores.map((score) => {
        const scoreId = score.scoreId;
        const variationId = score.variationId;

        const startingPost = getAPScoreFromPost(actionPlan.startSsqPost, scoreId);

        const _postsWithoutAP = actionPlan.actionPlanSsqPosts.map((p) => p.ssqPost);
        const postsWithoutAP = translatePostToAggPosts(_postsWithoutAP, scoreId);

        const postsWithAP = translatePostToAggPosts(actionPlan.simulationSsqPosts, scoreId);

        const scoreTarget = actionPlan.actionPlanScoreTargets.find((scoreTarget) => {
            const targetVarID = scoreTarget.variationId ?? null;
            return scoreTarget.scoreId === scoreId && targetVarID === variationId;
        });

        const goal = computeGoal({
            scoreTarget,
            startingPost,
            postWithoutAP: postsWithoutAP.lastItem,
        });

        const actionPlanMetadata: APMetadata = {
            actionPlan,
            goal,
            startingPost,
            scoresWithActionPost: postsWithAP,
            scoresWithoutActionPost: postsWithoutAP,
        };

        const actionPlanData: ActionPlanData = {
            scoreId,
            variationId,
            metadata: actionPlanMetadata,
        };
        return actionPlanData;
    });

    return actionPlanByScores;
};

type ScoreTarget = {
    value: number;
    percent: number;
};
const getFilledScoreTarget = (
    startingValue: number,
    targetValue: number,
    scoreTarget: ActionPlanScoreTargets,
): ScoreTarget | null => {
    const absolute = scoreTarget.simulationVaryAbsolute;
    const percent = scoreTarget.simulationVaryPercentage;

    if (absolute === undefined && percent === undefined) {
        return null;
    }

    if (absolute === undefined) {
        const nbPercent = percent as number;
        const growth = (startingValue / 100) * nbPercent;

        const valueFromPercent = targetValue - growth;
        return {
            percent: nbPercent,
            value: valueFromPercent,
        };
    }
    const nbValue = absolute as number;

    const percentFromValue = startingValue > 0 ? 100 - (nbValue / startingValue) * 100 : 0;

    return {
        percent: percentFromValue,
        value: nbValue,
    };
};

export type ComputeGoalOptions = {
    startingPost: APScore;
    postWithoutAP: APScore | null;
    scoreTarget: ActionPlanScoreTargets | undefined;
};
const computeGoal = (options: ComputeGoalOptions): Goal | null => {
    const { startingPost, postWithoutAP, scoreTarget } = options;

    if (scoreTarget === undefined) return null;
    if (postWithoutAP === null) return null;

    const startingValue = OlapScoresService.normalizeNumericalScore(startingPost.score, 0);
    const targetValue = postWithoutAP.score as number;
    const filledScoreTarget = getFilledScoreTarget(startingValue, targetValue, scoreTarget);
    if (filledScoreTarget === null) return null;

    const totalDiff = filledScoreTarget.value !== null ? startingValue + filledScoreTarget.value : null;
    if (totalDiff === null) return null;

    const goalReductionInPercent = startingValue > 0 ? 100 - (filledScoreTarget.value / startingValue) * 100 : 0;

    const completionPercentage =
        typeof totalDiff === "number" ?
            totalDiff > 0 ?
                100 - (1000 / totalDiff) * 100
            :   0
        :   0;

    const goal: Goal = {
        value: totalDiff,
        completionPercentage: completionPercentage,
        goalReductionPercentage: goalReductionInPercent,
    };
    return goal;
};

// Are those below ONLY Action based ?!
// I wonder
// Since, it takes `[main]` dimension...
type PostWithCampaignAndScores = SsqPost;
const getAPScoreFromPost = (post: PostWithCampaignAndScores, scoreId: number): APScore => {
    const score = post.olapScores?.[scoreId]?.score.value ?? null;
    const postCampaignLabel = post.campaign?.label ?? "";

    const dd = post.olapScores[scoreId];
    const actionPlanScore: APScore = {
        id: post.id,
        ssqVersionId: post.ssqVersionId,
        campaign: post.campaign,
        score: score,
        scoreByLabel: postCampaignLabel,
        score2: dd,
    };
    return actionPlanScore;
};

const translatePostToAggPosts = (posts: PostWithCampaignAndScores[], scoreId: number): AggPosts => {
    const postsDetails = posts.map((post) => {
        const actionPlanScore = getAPScoreFromPost(post, scoreId);
        return actionPlanScore;
    });

    if (postsDetails.length === 0) {
        return {
            lastItem: null,
            details: [],
        };
    }

    const lastItemIndex = postsDetails.length - 1;
    return {
        lastItem: postsDetails[lastItemIndex],
        details: postsDetails,
    };
};
